import type { PayloadAction } from "@reduxjs/toolkit";

/**
 * Shared parts of PosSettingsSlice — plan §5.2. The two apps agree on very little here:
 * ONE state field and ONE reducer body.
 *
 * Authored per platform instead:
 * - `savePosSettings` — a no-op on both sides, but the ARITY differs: web
 *   `(state, actions) => {}`, mobile `() => {}`. Type-level, not runtime (ledger hazard 14):
 *   RTK's creator always accepts an argument, but a 0-param reducer generates a creator whose
 *   TYPE rejects one. Preserved verbatim on both sides — web dispatches
 *   `savePosSettings({ dataToPost })` from four call sites, mobile calls
 *   `savePosSettings({...})` from two despite the 0-arity signature, so unifying either
 *   direction changes what typechecks. Note the web param is spelled `actions`, not `action`.
 * - web's four extra reducers (`setModuleSettingsLoaded`, `updatePosSettingsSlice`,
 *   `setPinnedChannels`, `setTableViewPinnedChannel`) and its `pinnedChannelData` /
 *   `tableViewPinnedChannel` state.
 * - mobile USED to add two reducers (`fetchItemLabelSettingsMetaData`,
 *   `fetchItemLabelSettingsMetaDataSuccess`) and `templateFieldsData` / `barcodeFieldsData`
 *   state. **Deleted 2026-09-11** — the label-settings screen that dispatched them now fetches
 *   its picklists directly. Mobile's variant therefore adds no state of its own any more; see
 *   `index.native.ts` for the persistence note that makes the deletion safe.
 * - web's `userLogout`/`supportLogout` `extraReducers`; mobile registers none at all. Web's
 *   `userLogout` case is EMPTY on purpose — see index.desktop.ts.
 *
 * ⚠️ `action` must be `PayloadAction<any>`, never `any` — see the ledger. `any` makes RTK
 * infer the NO-PAYLOAD overload, so every `dispatch(x(payload))` stops compiling. Reducers
 * the apps wrote with no `action` param at all keep that shape.
 */
export interface SharedPosSettingsState {
  defaultModuleSettings: any;
}

export const sharedInitialState: SharedPosSettingsState = {
  defaultModuleSettings: {},
};

export const sharedReducers = {
  saveDefaultModuleSettings: (
    state: SharedPosSettingsState,
    action: PayloadAction<any>
  ) => {
    state.defaultModuleSettings = action.payload;
  },
};
